import { useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { ModalContext } from 'components/ModalProvider/ModalProvider';
import { actions } from 'features/users/redux';

import { useAgentPasswordComplexity } from 'shared/hooks/useAgentPasswordComplexity';

const useCreateAgent = (isMobile = false) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const { openModal, closeModal } = useContext(ModalContext);

  const [form, setForm] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    name: '',
    surname: '',
    commission: 1,
  });

  const [withdrawalAllowed, setWithdrawalAllowed] = useState(true);
  const [depositAllowed, setDepositAllowed] = useState(true);
  const [onlyPlayerCreationAllowed, setOnlyPlayerCreationAllowed] = useState(false);
  const [semiAutomaticPayment, setSemiAutomaticPayment] = useState(true);

  const formData = useMemo(() => {
    const commission = form.commission || 0;

    return { ...form, withdrawalAllowed, onlyPlayerCreationAllowed, depositAllowed, commission, semiAutomaticPayment };
  }, [form, withdrawalAllowed, onlyPlayerCreationAllowed, depositAllowed, semiAutomaticPayment]);

  const passwordComplexity = useAgentPasswordComplexity({
    password: form.password,
    locale,
  });

  const disabled =
    form.confirmPassword !== form.password ||
    form.username.length === 0 ||
    !passwordComplexity.isValid;

  const onSubmit = useCallback(
    e => {
      if (e) e.preventDefault();

      if (disabled) return;

      if (!isMobile) closeModal();

      dispatch(
        actions.createAgent({
          form: formData,
          callback: () => history.push('/users/all'),
        }),
      );
    },
    [closeModal, disabled, dispatch, formData, history, isMobile],
  );

  const changeValue = useCallback(
    e => {
      setForm({ ...form, [e.currentTarget.name]: e.currentTarget.value?.trim() });
    },
    [form],
  );

  const changeValueUsername = useCallback(
    e => {
      setForm({ ...form, username: e.currentTarget.value?.replace(/\s/g, '') });
    },
    [form],
  );
  const changeCommission = useCallback(
    e => {
      const value = e.currentTarget.value?.trim();
      const reg = /^[1-9][0-9]*$/;
      if ((reg.test(value) || !value.length) && value <= 100) {
        setForm({ ...form, commission: e.currentTarget.value?.trim() });
      }
    },
    [form],
  );

  const isPasswordInvalid = form.password.length > 0 && !passwordComplexity.isValid;

  return {
    output: {
      form,
      disabled,
      withdrawalAllowed,
      depositAllowed,
      onlyPlayerCreationAllowed,
      semiAutomaticPayment,
      isPasswordInvalid,
      passwordRequirementsTooltip: passwordComplexity.requirementsTooltip,
    },
    input: {
      setSemiAutomaticPayment,
      setWithdrawalAllowed,
      setDepositAllowed,
      setOnlyPlayerCreationAllowed,
      changeCommission,
      changeValue,
      onSubmit,
      changeValueUsername,
      openModal,
      closeModal,
    },
  };
};

export default useCreateAgent;
